import { createSlice } from "@reduxjs/toolkit";
import {
  getShopSettings,
  patchShopSetting,
  getShopThemeSchema,
  getThemeAiPalettes,
  generateThemeAiPalettes,
  getThemeAiChat,
  sendThemeAiMessage,
  clearThemeAiChat,
  getThemeAiPrompt,
} from "./shopSettingsActions";

const initialState = {
  loading: false,
  error: null,
  success: false,
  shopSettings: null,
  // [CANA-547] GET /shop_settings/theme_schema/ — groups + fields the Theme
  // Editor renders from. Kept apart from the form's loading/error so a
  // schema failure never blocks the Shop Settings form.
  themeSchema: { loading: false, error: null, data: null },
  // [CANA-555] AI colors (backend CANA-554). One quota slot: every AI
  // response that carries `quota` writes it here so all sub-tabs agree.
  // `disabled` flips on a 503 (AI_THEME_ENABLED off on that environment).
  themeAi: {
    palettes: { loading: false, error: null, items: [], generatedAt: null },
    chat: {
      loading: false,
      error: null,
      messages: [],
      hasMore: false,
      sending: false,
      clearing: false,
    },
    prompt: { loading: false, error: null, text: null },
    quota: null,
    disabled: false,
  },
};

const takeQuota = (state, payload) => {
  if (payload && typeof payload === "object" && "quota" in payload) {
    state.themeAi.quota = payload.quota;
  }
  if (payload?.status === 503) state.themeAi.disabled = true;
};

const shopSettingsSlice = createSlice({
  name: "shop_settings",
  initialState,
  reducers: {
    resetShopSettingError: (state, action) => {
      if (action?.payload) {
        delete state.error[action?.payload];
        if (Object.keys(state.error)?.length === 0) {
          state.error = null;
        }
      } else {
        state.error = null;
      }
      state.loading = false;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getShopSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getShopSettings.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.shopSettings = payload[0] ? payload[0] : null;
      })
      .addCase(getShopSettings.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(patchShopSetting.pending, (state) => {
        state.loading = true;
      })
      .addCase(patchShopSetting.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.shopSettings = payload;
      })
      .addCase(patchShopSetting.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(getShopThemeSchema.pending, (state) => {
        state.themeSchema.loading = true;
        state.themeSchema.error = null;
      })
      .addCase(getShopThemeSchema.fulfilled, (state, { payload }) => {
        state.themeSchema.loading = false;
        state.themeSchema.data = payload;
      })
      .addCase(getShopThemeSchema.rejected, (state, { payload }) => {
        state.themeSchema.loading = false;
        state.themeSchema.error = payload;
      })
      // ---- [CANA-555] AI colors ------------------------------------------
      .addCase(getThemeAiPalettes.pending, (state) => {
        state.themeAi.palettes.loading = true;
        state.themeAi.palettes.error = null;
      })
      .addCase(getThemeAiPalettes.fulfilled, (state, { payload }) => {
        state.themeAi.palettes.loading = false;
        state.themeAi.palettes.items = payload?.palettes ?? [];
        state.themeAi.palettes.generatedAt = payload?.generated_at ?? null;
      })
      .addCase(getThemeAiPalettes.rejected, (state, { payload }) => {
        state.themeAi.palettes.loading = false;
        state.themeAi.palettes.error = payload;
        takeQuota(state, payload);
      })
      .addCase(generateThemeAiPalettes.pending, (state) => {
        state.themeAi.palettes.loading = true;
        state.themeAi.palettes.error = null;
      })
      .addCase(generateThemeAiPalettes.fulfilled, (state, { payload }) => {
        state.themeAi.palettes.loading = false;
        state.themeAi.palettes.items = payload?.palettes ?? [];
        state.themeAi.palettes.generatedAt = payload?.generated_at ?? null;
        takeQuota(state, payload);
      })
      .addCase(generateThemeAiPalettes.rejected, (state, { payload }) => {
        state.themeAi.palettes.loading = false;
        state.themeAi.palettes.error = payload;
        takeQuota(state, payload);
      })
      .addCase(getThemeAiChat.pending, (state) => {
        state.themeAi.chat.loading = true;
        state.themeAi.chat.error = null;
      })
      .addCase(getThemeAiChat.fulfilled, (state, { payload, meta }) => {
        state.themeAi.chat.loading = false;
        const page = payload?.messages ?? [];
        // A "before" page is older history: it goes in front of what is shown.
        state.themeAi.chat.messages = meta?.arg?.before
          ? [...page, ...state.themeAi.chat.messages]
          : page;
        state.themeAi.chat.hasMore = Boolean(payload?.has_more);
      })
      .addCase(getThemeAiChat.rejected, (state, { payload }) => {
        state.themeAi.chat.loading = false;
        state.themeAi.chat.error = payload;
        takeQuota(state, payload);
      })
      .addCase(sendThemeAiMessage.pending, (state) => {
        state.themeAi.chat.sending = true;
        state.themeAi.chat.error = null;
      })
      .addCase(sendThemeAiMessage.fulfilled, (state, { payload }) => {
        state.themeAi.chat.sending = false;
        const rows = [payload?.user_message, payload?.message].filter(Boolean);
        state.themeAi.chat.messages = [...state.themeAi.chat.messages, ...rows];
        takeQuota(state, payload);
      })
      .addCase(sendThemeAiMessage.rejected, (state, { payload }) => {
        state.themeAi.chat.sending = false;
        state.themeAi.chat.error = payload;
        takeQuota(state, payload);
      })
      .addCase(clearThemeAiChat.pending, (state) => {
        state.themeAi.chat.clearing = true;
      })
      .addCase(clearThemeAiChat.fulfilled, (state) => {
        state.themeAi.chat.clearing = false;
        state.themeAi.chat.messages = [];
        state.themeAi.chat.hasMore = false;
      })
      .addCase(clearThemeAiChat.rejected, (state, { payload }) => {
        state.themeAi.chat.clearing = false;
        state.themeAi.chat.error = payload;
      })
      .addCase(getThemeAiPrompt.pending, (state) => {
        state.themeAi.prompt.loading = true;
        state.themeAi.prompt.error = null;
      })
      .addCase(getThemeAiPrompt.fulfilled, (state, { payload }) => {
        state.themeAi.prompt.loading = false;
        state.themeAi.prompt.text = payload?.prompt ?? "";
      })
      .addCase(getThemeAiPrompt.rejected, (state, { payload }) => {
        state.themeAi.prompt.loading = false;
        state.themeAi.prompt.error = payload;
        takeQuota(state, payload);
      });
  },
});
export const { resetShopSettingError } = shopSettingsSlice.actions;
export default shopSettingsSlice.reducer;
